import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapPin, Building2, Globe } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "../services/apiClient";

const DeveloperProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [developer, setDeveloper] = useState(null);
  const [properties, setProperties] = useState([]);
  const [developments, setDevelopments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDeveloper();
    fetchDeveloperProperties();
    fetchDeveloperDevelopments();
  }, [id]);

  const fetchDeveloper = async () => {
    try {
      const response = await api.get(`/developers/${id}`, { auth: false });
      const data = await response.json();
      setDeveloper(data);
    } catch (error) {
      console.error("Error fetching developer:", error);
      toast.error("Error al cargar perfil de desarrollador");
    } finally {
      setLoading(false);
    }
  };

  const fetchDeveloperProperties = async () => {
    try {
      const response = await api.get(`/developers/${id}/properties`, {
        auth: false,
      });
      const data = await response.json();
      setProperties(data);
    } catch (error) {
      console.error("Error fetching developer properties:", error);
    }
  };

  const fetchDeveloperDevelopments = async () => {
    try {
      const response = await api.get(`/developers/${id}/developments`, {
        auth: false,
      });
      const data = await response.json();
      setDevelopments(data);
    } catch (error) {
      console.error("Error fetching developer developments:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!developer) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <p className="text-lg text-muted-foreground">
            Desarrollador no encontrado
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />

      <div className="bg-gradient-to-br from-primary/10 to-secondary/10 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center space-y-6 md:space-y-0 md:space-x-8">
            {developer.image_url && (
              <img
                src={developer.image_url}
                alt={developer.name}
                className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
              />
            )}
            <div className="text-center md:text-left">
              <h1
                className="text-4xl md:text-5xl font-bold mb-4 tracking-tight"
                style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
                data-testid="developer-name"
              >
                {developer.name}
              </h1>
              <div className="flex flex-wrap items-center justify-center md:justify-start space-x-4 text-muted-foreground">
                {developer.years_active && (
                  <span className="flex items-center">
                    <Building2 className="w-4 h-4 mr-2" />
                    {developer.years_active} años de experiencia
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          <div className="lg:col-span-2">
            <h2
              className="text-3xl font-bold mb-6"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Acerca de
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-8">
              {developer.description}
            </p>

            {developer.specialties && developer.specialties.length > 0 && (
              <div>
                <h3
                  className="text-xl font-bold mb-4"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  Especialidades
                </h3>
                <div className="flex flex-wrap gap-2">
                  {developer.specialties.map((specialty, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-accent/10 text-accent rounded-full text-sm font-medium"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="bg-muted rounded-lg p-6">
            <h3
              className="text-xl font-bold mb-4"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Contacto
            </h3>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Para contactar a este desarrollador, usa el formulario de
                contacto en las propiedades o desarrollos listados.
              </p>
              <p className="text-xs text-muted-foreground italic">
                La comunicación se realiza a través de la plataforma para tu
                seguridad.
              </p>
            </div>
          </div>
        </div>

        <div>
          <h2
            className="text-3xl font-bold mb-8"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Propiedades ({properties.length})
          </h2>

          {properties.length === 0 ? (
            <div className="text-center py-12 bg-muted rounded-lg">
              <p className="text-muted-foreground">
                Aún no hay propiedades listadas por este desarrollador
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {properties.map((property) => (
                <div
                  key={property.id}
                  onClick={() => navigate(`/property/${property.id}`)}
                  className="bg-background border border-border/40 rounded-lg overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:border-primary/40 transition-all duration-300 cursor-pointer"
                  data-testid={`property-card-${property.id}`}
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={
                        property.image_urls?.[0] ||
                        "https://images.pexels.com/photos/35193584/pexels-photo-35193584.jpeg"
                      }
                      alt={property.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs uppercase tracking-wider font-semibold text-accent">
                        {property.category}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {property.status}
                      </span>
                    </div>
                    <h3
                      className="text-lg font-bold mb-1"
                      style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                      {property.title}
                    </h3>
                    <p className="text-xs text-muted-foreground mb-3 flex items-center">
                      <MapPin className="w-3 h-3 mr-1" />
                      {property.address}
                    </p>
                    <p className="text-xl font-bold mono">
                      ${property.price_mxn?.toLocaleString()} MXN
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sección de Desarrollos */}
        <div className="mt-16">
          <h2
            className="text-3xl font-bold mb-8"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Desarrollos ({developments.length})
          </h2>

          {developments.length === 0 ? (
            <div className="text-center py-12 bg-muted rounded-lg">
              <p className="text-muted-foreground">
                Aún no hay desarrollos listados por este desarrollador
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {developments.map((development) => (
                <div
                  key={development.id || development._id}
                  onClick={() =>
                    navigate(
                      `/development/${development.id || development._id}`,
                    )
                  }
                  className="bg-background border border-border/40 rounded-lg overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:border-primary/40 transition-all duration-300 cursor-pointer"
                  data-testid={`development-card-${development.id || development._id}`}
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={development.cover_image_url}
                      alt={development.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <h3
                      className="text-lg font-bold mb-2 line-clamp-2"
                      style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                      {development.name}
                    </h3>
                    <p className="text-sm font-semibold text-accent mb-2">
                      ${development.min_price_mxn.toLocaleString()} - $
                      {development.max_price_mxn.toLocaleString()} MXN
                    </p>
                    <p className="text-xs text-muted-foreground mb-3 flex items-center">
                      <MapPin className="w-3 h-3 mr-1" />
                      {development.address}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {development.models?.length || 0} modelos disponibles
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DeveloperProfile;
